import React, { useState } from 'react';
import { Routes, Route, NavLink, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { withRoleAccess } from '../../components/auth/withRoleAccess';
import { ROLES } from '../../hooks/useAuthorization';

// Admin Pages
import CourseManagement from './CourseManagement';
import UserManagement from './UserManagement';

const AdminContainer = styled.div`
  display: flex;
  min-height: calc(100vh - 60px); // Account for navbar height
`;

const Sidebar = styled.aside`
  width: 280px;
  background-color: ${theme.colors.background};
  border-right: 1px solid ${theme.colors.light};
  padding: ${theme.spacing.md};
  display: flex;
  flex-direction: column;
`;

const SidebarHeader = styled.div`
  margin-bottom: ${theme.spacing.lg};
  
  h2 {
    font-size: ${theme.fontSizes.large};
    margin-bottom: ${theme.spacing.xs};
  }
  
  p {
    color: ${theme.colors.gray};
    font-size: ${theme.fontSizes.small};
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.md};
  margin-top: auto;
  margin-bottom: ${theme.spacing.md};
  background-color: ${theme.colors.light};
  border: none;
  border-radius: ${theme.radii.medium};
  color: ${theme.colors.dark};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  &:hover {
    background-color: ${theme.colors.gray};
    text-decoration: none;
  }
`;

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NavItem = styled.li`
  margin-bottom: ${theme.spacing.xs};
`;

interface StyledNavLinkProps {
  to: string;
}

const StyledNavLink = styled(NavLink)<StyledNavLinkProps>`
  display: flex;
  align-items: center;
  padding: ${theme.spacing.md};
  color: ${theme.colors.dark};
  text-decoration: none;
  border-radius: ${theme.radii.medium};
  transition: all 0.2s;
  
  svg {
    margin-right: ${theme.spacing.sm};
    width: 20px;
    height: 20px;
  }
  
  &:hover {
    background-color: ${theme.colors.light};
    text-decoration: none;
  }
  
  &.active {
    color: ${theme.colors.primary};
    background-color: rgba(74, 144, 226, 0.1);
    font-weight: 600;
  }
`;

const ContentArea = styled.main`
  flex: 1;
  padding: ${theme.spacing.lg};
  background-color: ${theme.colors.background};
  overflow-y: auto;
`;

const AdminDashboard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  return (
    <AdminContainer>
      <Sidebar>
        <SidebarHeader>
          <h2>Admin Dashboard</h2>
          <p>Manage your app's content and settings</p>
        </SidebarHeader>
        
        <NavList>
          <NavItem>
            <StyledNavLink to="/admin">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
              </svg>
              Course Management
            </StyledNavLink>
          </NavItem>
          
          <NavItem>
            <StyledNavLink to="/admin/users">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
              User Management
            </StyledNavLink>
          </NavItem>
        </NavList>

        <BackButton onClick={() => navigate('/')}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Main Site
        </BackButton>
      </Sidebar>
      
      <ContentArea>
        <Routes>
          <Route index element={<CourseManagement />} />
          <Route path="courses" element={<CourseManagement />} />
          <Route path="courses/:courseId/lessons/*" element={<CourseManagement />} />
          <Route path="users" element={<UserManagement />} />
        </Routes>
      </ContentArea>
    </AdminContainer>
  );
};

export default withRoleAccess(ROLES.APP_EDITOR)(AdminDashboard); 